import React, { ReactNode, useEffect } from 'react';
import './App.css';
import { Provider } from 'react-redux';
import store from '@diditusa/shared/store';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import Login from './pages/Login/login';
import JobsCreate from './pages/Jobs/jobs.create';
import ReviewForm from './pages/ReviewForm/ReviewForm';
import CompanyList from './pages/SecurityCompanies/CompanyList';
import CompanyDetail from './pages/SecurityCompanies/CompanyDetail';
import AccountPage from './pages/AccountPage/AccountPage';
import OrderForm from './pages/OrderForm/OrderForm';
import Checkout from './pages/Checkout/Checkout';
import CheckoutResult from './pages/Checkout/CheckoutResult';
import { AuthProvider } from './context/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';
import EmployeeRoute from './components/EmployeeRoute';
import ManagerRoute from './components/ManagerRoute';
import CompanyAgents from './pages/CompanyPages/CompanyAgents';
import CompanyJobs from './pages/CompanyPages/CompanyJobs';
import EmployeeJobs from './pages/EmployeePages/EmployeeJobs';
import ComparisonPage from './pages/ComparisonPage/ComparisonPage';
import CompanyJobDetails from './pages/CompanyPages/CompanyJobDetails';
import AgentProfilePage from './pages/CompanyPages/CompanyAgentDetails';
import AgentProfileEdit from './pages/CompanyPages/CompanyAgentDetailsEdit';
import PreviousOrders from './pages/Previous Orders/PreviousOrders';
import HomeSwitch from './pages/Home/home.switch';
import ComingSoon from './pages/ComingSoonPage/ComingSoon';
import JobAgentDetails from './pages/Jobs/jobs.agents';
import LandingPage from './pages/Landing/LandingPage';
import ComparisonPageStandalone from './pages/ComparisonPage/ComparisonPageStandalone';
import ReviewPage from './pages/ReviewPage/ReviewPage';
import JobDetailsPage from './pages/Jobs/jobs.details';
import InviteRedirect from './pages/InviteToken/inviteToken';
import HowItWorksPage from './pages/HowItWorks/howItWorks';
import PrivacyPolicy from './pages/Policies/PrivacyPolicy';
import Refund from './pages/Policies/Refund';
import ContactUs from './pages/ContactUs/ContactUs';
import AboutUs from './pages/AboutUs/AboutUs';

const NotFoundPage = () => {
  return (
    <div>
      <h1>Page Not Found</h1>
    </div>
  );
};

function App() {
  // Wrapper component that sets the title of the browser tab
  const WithTitle: React.FC<{ element: React.ReactElement; title: string }> = ({
    element,
    title,
  }) => {
    useEffect(() => {
      document.title = title;
    }, [title]);
    return element;
  };
  return (
    <div className='App'>
      <BrowserRouter>
        <Provider store={store}>
          <AuthProvider>
            <Routes>
              <Route path='/' element={<Layout />}>
                <Route
                  index
                  element={<WithTitle element={<LandingPage />} title={'Coming soon'} />}
                />
                <Route path='/invites/:token' element={<InviteRedirect />} />
                <Route
                  path='/how'
                  element={<WithTitle element={<HowItWorksPage />} title='How it works' />}
                />
                <Route path='/about' element={<WithTitle element={<AboutUs />} title='About' />} />
                <Route
                  path='/contact'
                  element={<WithTitle element={<ContactUs />} title='Contact' />}
                />
                <Route element={<PrivateRoute />}>
                  <Route
                    path='/home'
                    element={<WithTitle element={<HomeSwitch />} title='Dashboard' />}
                    handle={{ title: 'Dashboard' }}
                  />
                  <Route path='/login' element={<WithTitle element={<Login />} title='Login' />} />
                  <Route
                    path='/jobs/create'
                    element={<WithTitle element={<JobsCreate />} title='New Job' />}
                  />
                  <Route
                    path='/jobs/:jobId'
                    element={<WithTitle element={<JobDetailsPage />} title='Job Details' />}
                  />
                  {/* <Route path='/review' element={<ReviewForm />} /> */}
                  <Route
                    path='/account'
                    element={<WithTitle element={<AccountPage />} title='Account' />}
                  />
                  <Route path='/companies' element={<CompanyList />} />
                  <Route path='/company/:id' element={<CompanyDetail />} />
                  <Route path='/tier-selected/:productId/:productTitle' element={<OrderForm />} />
                  <Route
                    path='/new-order/:productId/:productTitle'
                    element={<WithTitle element={<ComparisonPage />} title='Comparison' />}
                  />
                  <Route
                    path='/comparison'
                    element={
                      <WithTitle element={<ComparisonPageStandalone />} title='Comparison' />
                    }
                  />
                  <Route
                    path='/review'
                    element={<WithTitle element={<ReviewPage />} title='Review Order' />}
                  />
                  <Route
                    path='/previousOrders'
                    element={<WithTitle element={<PreviousOrders />} title='Previous Orders' />}
                  />
                  <Route
                    path='/checkout/:jobId'
                    element={<WithTitle element={<Checkout />} title='Checkout' />}
                  />
                  <Route
                    path='/return'
                    element={<WithTitle element={<CheckoutResult />} title='Checkout Complete' />}
                  />
                  <Route
                    path='/privacy-policy'
                    element={<WithTitle element={<PrivacyPolicy />} title='Privacy Policy' />}
                  />
                  <Route
                    path='/refund'
                    element={<WithTitle element={<Refund />} title='Refund' />}
                  />
                  <Route
                    path='/contact'
                    element={<WithTitle element={<ContactUs />} title='Contact' />}
                  />
                  <Route
                    path='/about'
                    element={<WithTitle element={<AboutUs />} title='About' />}
                  />

                  <Route element={<EmployeeRoute />}>
                    <Route path='/agent/jobs' element={<EmployeeJobs />} />
                    <Route element={<ManagerRoute />}>
                      <Route
                        path='/company/jobs'
                        element={<WithTitle element={<CompanyJobs />} title='Jobs' />}
                      />
                      <Route
                        path='/company/jobs/:jobId'
                        element={<WithTitle element={<CompanyJobDetails />} title='Job Details' />}
                      />
                      <Route
                        path='/company/agents'
                        element={<WithTitle element={<CompanyAgents />} title='Agents' />}
                      />

                      <Route
                        path='/company/agents/:agentId/edit'
                        element={<WithTitle element={<AgentProfileEdit />} title='Agent Edit' />}
                      />
                      <Route
                        path='/company/agents/:agentId'
                        element={<WithTitle element={<AgentProfilePage />} title='Agent Profile' />}
                      />
                      <Route path='/company' element={<div>Company details</div>} />
                    </Route>
                  </Route>
                </Route>
                <Route path='*' element={<NotFoundPage />} />
              </Route>
            </Routes>
          </AuthProvider>
        </Provider>
      </BrowserRouter>
    </div>
  );
}

export default App;
